/*
 * Confidential and Proprietary.
 * Do not distribute without Bloom Harbor Gifts consent.
 * Copyright Bloom Harbor Gifts 2019. All rights reserved.
 */

import React, { useState, useEffect } from 'react';
import Grid from '@material-ui/core/Grid';
import Modal from '@material-ui/core/Modal';
import withStyles from '@material-ui/core/styles/withStyles';
import {
    object, bool, func, string, shape, arrayOf, number,
} from 'prop-types';
import Helmet from 'react-helmet';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import mbpLogger from '@module-logger/mbp';
import { useQuery } from '@apollo/client';
import PersonalizeCard from './PersonalizeView/PersonalizeCard';
import ReviewCard from './ReviewCard/ReviewCard';
import GraphqlOccasionDataContainer from './SelectTemplateView/GraphqlOccasionDataContainer';
import { fetchGCIKey } from '../../../../state/ducks/App/App-Actions';
import { addCGCDataToApi } from '../../../../state/ducks/CGC/CGC-Actions';
import { getCGCCardData } from '../../../../state/ducks/App/App-Selectors';
import CGCModalHeader from './CGCModalHeader';
import CelebrationCardTopView from './CelebrationCardTopView';
import GraphqlPortalLoader from '../../../components/GraphqlComponents/GraphqlCommonComponents/GraphqlPortalLoader';
import findContent from '../../../gql/queries/findContent';
import { DefaultTools } from './RTE/Config/Default';
import { getRecipientGiftMessageError } from '../../../../state/ducks/Checkout/ducks/Common/Common-Selectors';
import { clearErrors } from '../../../../state/ducks/Common/ducks/Error/Error-Actions';
import { clearOrderItemErrors } from '../../../../state/ducks/Checkout/ducks/Common/ducks/Error/Error-Actions';
import { getFeatureFlags } from '../../../../state/ducks/Checkout/ducks/Global/Global-Selectors';
import { getOrderItemErrors } from '../../../../state/ducks/Checkout/ducks/Common/ducks/Error/Error-Selector';

const styles = (theme) => ({
    container: {
        position: 'absolute',
        top: 50,
        left: '50%',
        transform: 'translateX(-50%)',
        maxWidth: 1280,
        width: '80%',
        height: '90vh',
        borderRadius: 5,
        border: `1px solid ${theme.palette.cgc?.borderColor || '#666'}`,
        background: theme.palette.cgc?.bgColor || 'white',
        '&:focus': {
            outline: 'none',
        },
        [theme.breakpoints.down('sm')]: {
            minWidth: '100%',
            height: '100%',
            top: 0,
        },
    },
});

const MODAL_VIEWS = {
    SELECT_TEMPLATE: 'select',
    PERSONALIZE: 'personalize',
    REVIEW: 'review',
};

const CGCModal = ({
    classes,
    openModal,
    closeModal,
    occasionData,
    cartItemId,
    productSku,
    brandData,
    // brandUid is the UID of the brand in contentstack - Helps query the data from contentstack
    brandUid,
    brand,
    addCGCData,
    editCard,
    featureFlags,
    getCGCKey,
    setOpenGreetingMessage,
    giftMessageErrors,
    orderItemErrors,
    callClearOrderItemErrors,
    callClearErrors,
}) => {
    // Edit card -- if the Modal is edit view
    // gets the data from state and checks if the data exist
    const {
        cgcCard, orderItemId, cgcMessage,
    } = editCard;
    // Celebrations Modal State - The data is being stored in the state and will be updated to redux after clicking on Add To Cart
    const [modalView, setModalView] = useState(cgcCard ? MODAL_VIEWS.PERSONALIZE : MODAL_VIEWS.SELECT_TEMPLATE);
    const [selectedOccasion, setOccasion] = useState('');
    const [selectedCard, setCardTemplate] = useState({});
    const [messageData, setMessageData] = useState({});
    const [isEditMode, setEditMode] = useState(false);
    const [occasionList, setOccasionList] = useState({});
    const  fonts = [];

    const resetState = () => {
        setCardTemplate({});
        setMessageData({});
        setOccasion('');
    };

    const clearErrorMessages = () => {
        callClearErrors({
            errType: 'giftMessage',
            field: 'error',
        });

        callClearOrderItemErrors(
            cartItemId,
            'giftMessage',
            {
                errType: 'clearAllFields',
                field: '',
            },
        );
    };

    const getOccassionList = () => {
        const list = {};
        if (occasionData?.length > 0) {
            occasionData.forEach(
                (occasion) => {
                    list[occasion.occasions?.reference?.[0]?.uid] = occasion.occasions?.reference?.[0]?.title;
                },
            );
        }
        return list;
    };

    useEffect(() => {
        if (occasionData?.length > 0) {
            const filteredOccasion = getOccassionList();
            setOccasionList(filteredOccasion);
        }
    }, []);

    useEffect(() => {
        resetState();

        // get PersonalizationKey from API and store in redux
        const params = {
            orderItemId: cartItemId,
            brandCode: brand.id,
            brand: brand.domain,
        };

        if (!featureFlags['is-cgc-api-from-main-url']) {
            getCGCKey(params);
        }

        // set the state to the selected data if view is edit card
        if (orderItemId) {
            setCardTemplate(cgcCard);
            setMessageData(cgcMessage);
            setEditMode(true);
        }
    }, [cartItemId]);

    // onChange Handler for setting Greeting Card in state: Greeting Card Object
    const onSelectTemplate = (template) => {
        setCardTemplate(template);
        setModalView(MODAL_VIEWS.PERSONALIZE);
    };

    // onChange Handler for setting message in state: message Object, consists of => attributes(styles) and text
    const setCardMessage = (data) => {
        setMessageData(data);
    };

    const handleAfterSaveGCI = () => {
        setOpenGreetingMessage(false);
        closeModal(false);
    };

    // Add To Cart Function. Calls the saga operations and adds the card to the redux state and API
    const addToCart = () => {
        const cgcData = {
            cardType: featureFlags['is-cgc-api-from-main-url'] ? 'B2CGC' : 'GCI',
            orderItemId: cartItemId,
            cgcCard: selectedCard,
            cgcMessage: messageData,
            selectedOccasion,
            productSku,
            brandData,
            occasionList,
        };
        addCGCData({
            cardData: selectedCard,
            message: messageData,
            cgcData,
            callBackHandler: handleAfterSaveGCI,
        });
    };

    const handleCloseModal = () => {
        clearErrorMessages();

        closeModal(false);
    };

    const FONT_QUERY = findContent({
        brand: 'gci',
        contentType: 'fonts',
    });

    let response = null;

    const { data, error, loading } = useQuery(FONT_QUERY);
    const cartIconLabel = featureFlags['cart-icon-label']?.label || 'Cart';

    if (loading) {
        response = <GraphqlPortalLoader />;
    } if (error) {
        mbpLogger.logError({
            appName: process.env.npm_package_name,
            query: FONT_QUERY,
            component: 'PersonalizeCard.js',
            message: 'Failed to fetch content data for: content type fonts, on brand gci',
            error,
        });
        response = null;
    }
    const toolsFontData = [];
    if (data) {
        const toolsData = data?.findContent?.content?.entries;
        DefaultTools.forEach((tool) => {
            if (tool.value === 'font') {
                const fontData =  toolsData?.map((font, index) => {
                    const keyStyle = `style-${index}`;
                    fonts.push(<style key={keyStyle}>{`@font-face {font-family: ${font.family_name}; src: url('${font?.font_path?.href}'); font-display: swap;`}</style>);
                    return {
                        maxChar: font.max_char_cards,
                        label: font.title,
                        value: font.family_name,
                        maxCharsPerLine: font.max_chars_per_line_cards,
                        maxLines: font.max_lines_cards,
                    };
                });
                toolsFontData.push({ ...tool, list: fontData });
            } else if (toolsFontData.filter((toolData) => toolData?.value === tool?.value).length === 0) {
                toolsFontData.push({ ...tool });
            }
        });
    }

    response = (
        <Modal open={openModal}>
            <Grid className={classes.container}>
                {fonts?.length > 0 && <Helmet>{fonts}</Helmet>}
                <CGCModalHeader
                    closeModal={handleCloseModal}
                    headerTitle="Celebration&apos;s Greetings"
                />
                <CelebrationCardTopView
                    occasionData={occasionData}
                    selectedOccasion={selectedOccasion}
                    change={setOccasion}
                    setModalView={setModalView}
                    modalViews={MODAL_VIEWS}
                    selectedView={modalView}
                    selectedCard={selectedCard}
                    messageData={messageData}

                />
                {/* Renders the View based on the selected view in the state */}
                {modalView === MODAL_VIEWS.SELECT_TEMPLATE
                    ? (
                        <GraphqlOccasionDataContainer
                            occasions={occasionData}
                            brandUid={brandUid}
                            selectedOccasion={selectedOccasion}
                            setCardTemplate={onSelectTemplate}
                        />
                    )
                    : null}
                {modalView === MODAL_VIEWS.PERSONALIZE
                    ?             (
                        <PersonalizeCard
                            isEditMode={isEditMode}
                            setCardMessage={setCardMessage}
                            messageData={messageData}
                            selectedCard={selectedCard}
                            onSave={() => setModalView(MODAL_VIEWS.REVIEW)}
                            fonts={fonts}
                            toolsFontData={toolsFontData}
                        />
                    )
                    : null}
                {modalView === MODAL_VIEWS.REVIEW
                    ?             (
                        <ReviewCard
                            cartItemId={cartItemId}
                            brandData={brandData}
                            cardData={selectedCard}
                            messageData={messageData}
                            addToCart={addToCart}
                            giftMessageErrors={giftMessageErrors}
                            orderItemErrors={orderItemErrors}
                            clearErrorMessages={clearErrorMessages}
                            cartIconLabel={cartIconLabel}
                        />
                    )
                    : null}
            </Grid>
        </Modal>
    );
    return response;
};

CGCModal.propTypes = {
    classes: object.isRequired,
    openModal: bool.isRequired,
    closeModal: func.isRequired,
    brandData: shape({
        brand_logo: shape({
            url: string.isRequired,
        }),
        card_back_view_disclaimer: string.isRequired,
        back_card_text: string.isRequired,
        card_size_heigh: number.isRequired,
        card_size_width: number.isRequired,
        back_view_font_size: number.isRequired,

    }).isRequired,
    occasionData: arrayOf(
        shape({
            rank: number.isRequired,
            refrence: arrayOf(
                shape({
                    uid: string.isRequired,
                }),
            ).isRequired,
        }),
    ).isRequired,
    cartItemId: string.isRequired,
    brandUid: string.isRequired,
    brand: shape({
        domain: string,
    }).isRequired,
    addCGCData: func.isRequired,
    editCard: shape({
        orderItemId: string,
        cgcCard: shape({
            uid: string,
        }),
        cgcMessage: shape({
            style: shape({
                textAlign: string,
                fontFamily: string,
                color: string,
            }),
            text: string,
        }),
    }),
    featureFlags: object.isRequired,
    getCGCKey: func.isRequired,
    productSku: string.isRequired,
    setOpenGreetingMessage: func,
    giftMessageErrors: object,
    orderItemErrors: object,
    callClearErrors: func,
    callClearOrderItemErrors: func,
};

CGCModal.defaultProps = {
    editCard: {},
    setOpenGreetingMessage: () => {},
    giftMessageErrors: {},
    orderItemErrors: {},
};

const mapStateToProps = (state) => ({
    giftMessageErrors: getRecipientGiftMessageError(state),
    orderItemErrors: getOrderItemErrors(state),
    existingCgcData: getCGCCardData(state),
    featureFlags: getFeatureFlags(state),
});

const mapDispatchToProps = (dispatch) => ({
    addCGCData: bindActionCreators(addCGCDataToApi, dispatch),
    getCGCKey: bindActionCreators(fetchGCIKey, dispatch),
    callClearOrderItemErrors: bindActionCreators(clearOrderItemErrors, dispatch),
    callClearErrors: bindActionCreators(clearErrors, dispatch),
});

export default connect(mapStateToProps, mapDispatchToProps)(withStyles(styles)(CGCModal));
